// React imports
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useOutletContext, useParams, useNavigate, Navigate } from 'react-router-dom';

// Other imports
import axios from 'axios';
import _ from 'lodash';

// Material UI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { ThemeProvider } from '@mui/material/styles';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHouse, faPlug, faDiagramProject, faGrid2, faArrowLeft, faChevronRight, faListCheck, faMessages } from '@fortawesome/pro-light-svg-icons';

// Local imports
import { SUPER_ADMIN, buildConnectors, MONADA_APPS } from './elariomData.js';
import { SectionTitle, Stat, StatRow, ListRow } from './ElariomParts.js';
import elariomTheme, { loadElariomFonts, SERIF, MONO, INK, MUTED, PAPER, HAIRLINE } from './elariomTheme.js';
import ElariomConnectors from './ElariomConnectors.js';
import ElariomExecutions from './ElariomExecutions.js';
import ElariomAsk from './ElariomAsk.js';
import ElariomWorkflows from './ElariomWorkflows.js';

// Elariom: the agent-platform view of a workspace, at /organization/:alias/elariom.
// It is not linked from anywhere and only the Monada super-admin can open it; anyone else
// is sent back to the workspace as if the page did not exist. The workspace itself is
// unchanged for everyone, including the admin, outside this URL.

const SECTIONS = [
    { id: '', title: 'Home', icon: faHouse },
    { id: 'connectors', title: 'Connectors', icon: faPlug },
    { id: 'workflows', title: 'Workflows', icon: faDiagramProject },
    { id: 'executions', title: 'Executions', icon: faListCheck },
    { id: 'ask', title: 'Ask Elariom', icon: faMessages },
    { id: 'apps', title: 'Apps', icon: faGrid2 },
];

function Wordmark() {
    return <Typography className='elariom-serif' sx={{ fontFamily: SERIF, fontSize: '28px', fontWeight: 400, letterSpacing: '-0.01em', color: INK, lineHeight: 1 }}>Elariom</Typography>;
}

function NavItem({ section, active, onClick }) {
    return (
        <Box
            role='button'
            tabIndex={0}
            onClick={onClick}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onClick()}
            sx={{
                display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 2px', cursor: 'pointer',
                fontSize: '13px', whiteSpace: 'nowrap',
                color: active ? INK : MUTED,
                fontWeight: active ? 600 : 400,
                borderBottom: { xs: active ? `1px solid ${INK}` : '1px solid transparent', md: 'none' },
                '&:hover': { color: INK },
                '&:focus-visible': { outline: `1px solid ${INK}`, outlineOffset: '2px' },
            }}
        >
            <Box sx={{ width: '16px', display: 'flex', justifyContent: 'center', '& svg': { fontSize: '14px' } }}>
                <FontAwesomeIcon icon={section.icon} />
            </Box>
            {section.title}
            {active && <Box sx={{ display: { xs: 'none', md: 'block' }, marginLeft: 'auto', width: '5px', height: '5px', borderRadius: '50%', background: INK }} />}
        </Box>
    );
}

function greeting() {
    const h = new Date().getHours();
    return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

function Home({ data, connectors, runs, go }) {
    const connected = connectors.filter(c => c.group === 'workspace' && c.status === 'connected').length;
    const workspaceCount = connectors.filter(c => c.group === 'workspace').length;
    const lastRun = runs[0];
    const rates = _.sumBy(runs.filter(r => !r.error), r => r.rateCount || 0);
    const openGaps = _.sumBy(data?.rateImports || [], i => i.openGapCount || 0);

    const rows = [
        { id: 'connectors', icon: faPlug, title: 'Connectors', text: `${connected} of ${workspaceCount} workspace connectors on, ${connectors.filter(c => c.group === 'planned').length} planned.` },
        { id: 'workflows', icon: faDiagramProject, title: 'Workflows', text: `Parse supplier rate files${lastRun ? `, last run ${new Date(lastRun.at).toLocaleDateString()}` : ''}. Inquiry to accepted quote, RFQ to best offer and Booking to voyage completion, coming soon.` },
        { id: 'apps', icon: faGrid2, title: 'Apps', text: `${MONADA_APPS.length} Monada apps share this workspace's data.` },
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
            <SectionTitle
                title={`${greeting()}.`}
                blurb={`This is ${data?.workspace?.title || 'the workspace'} as an agent platform: what it is connected to, the workflows running on it, and the apps that share its data.`}
            />
            <StatRow>
                <Stat value={1} label='Workflows' hint='Parse supplier rate files' />
                <Stat value={runs.length} label='Agent runs' hint={lastRun ? `Last: ${new Date(lastRun.at).toLocaleString()}` : 'None yet on this workspace'} />
                <Stat value={rates.toLocaleString()} label='Rates parsed by the agent' hint='Across all runs, before review' />
                <Stat value={openGaps} label='Open gaps' hint='What the rules did not cover' />
            </StatRow>
            <Box>
                {rows.map(r => (
                    <ListRow key={r.id} icon={<FontAwesomeIcon icon={r.icon} />} title={r.title} subtitle={r.text} onClick={() => go(r.id)}
                        right={<FontAwesomeIcon icon={faChevronRight} style={{ fontSize: '14px', color: MUTED }} />} />
                ))}
            </Box>
        </Box>
    );
}

function Apps({ org }) {
    const navigate = useNavigate();
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <SectionTitle
                title='Apps'
                blurb='The screens people work in. Each Monada app reads and writes the same workspace data that workflows use, so a workflow can feed an app and an app can start a workflow.'
            />
            <Box>
                {MONADA_APPS.map(app => (
                    <ListRow key={app.title} icon={<FontAwesomeIcon icon={app.icon} />} title={app.title} subtitle={app.detail}
                        onClick={() => navigate(`/organization/${org.alias}${app.path}`)}
                        right={<FontAwesomeIcon icon={faChevronRight} style={{ fontSize: '14px', color: MUTED }} />} />
                ))}
            </Box>
            <Typography sx={{ fontSize: '12px', color: MUTED }}>
                Custom apps built for one workspace come later. The New button on Workflows shows how creating one will work.
            </Typography>
        </Box>
    );
}

function ElariomShell() {
    const navigate = useNavigate();
    const params = useParams();
    const { me, org, accesstoken, emailLoginInfo } = useOutletContext();
    const [ data, setData ] = useState(null);
    const [ error, setError ] = useState(null);

    const path = (params['*'] || '').split('/').filter(Boolean);
    const section = SECTIONS.find(s => s.id === (path[0] || '')) ? (path[0] || '') : '';
    const allowed = me?.email === SUPER_ADMIN;

    useEffect(() => {
        if (!allowed || !org?.id) return;
        axios.get(`/api/admin/elariom/${org.id}?accesstoken=${accesstoken}`)
            .then(({ data }) => setData(data))
            .catch(e => setError(e.response?.data?.detail || e.message));
    }, [allowed, org?.id, accesstoken]);

    const onTeamsChange = useCallback(teams => setData(d => ({ ...d, teams })), []);
    const connectors = useMemo(() => buildConnectors({ org, emailLoginInfo, platform: data?.platform, teams: data?.teams, whatsapp: data?.whatsapp, accesstoken }), [org, emailLoginInfo, data, accesstoken]);
    const runs = useMemo(() => _(data?.rateImports || [])
        .flatMap(i => (i.agentRuns || []).map(r => ({ ...r, importId: i.id, supplierOnImport: i.supplier, fileName: i.fileName })))
        .sortBy(r => -(r.at || 0))
        .value(), [data]);

    if (!allowed) {
        return <Navigate to={`/organization/${org.alias}`} replace />;
    }

    const go = id => navigate(`/organization/${org.alias}/elariom${id ? `/${id}` : ''}`);

    return (
        <Box sx={{
            display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: '100vh', background: PAPER, color: INK,
            // Monada's global styles set their own font on text and controls; inside Elariom, SF Mono.
            // (App.css sets Poppins on buttons and inputs with !important, so the same is needed here.)
            fontFamily: MONO,
            '& .MuiTypography-root:not(.elariom-serif)': { fontFamily: MONO },
            '& button, & input:not([type=date]), & textarea, & select, & a': { fontFamily: `${MONO} !important` },
        }}>
            <Box
                component='nav'
                aria-label='Elariom'
                sx={{
                    width: { xs: '100%', md: '232px' }, flexShrink: 0, boxSizing: 'border-box',
                    background: PAPER,
                    borderRight: { md: `1px solid ${HAIRLINE}` },
                    borderBottom: { xs: `1px solid ${HAIRLINE}`, md: 'none' },
                    padding: { xs: '14px 16px', md: '28px 24px' },
                    display: 'flex', flexDirection: 'column', gap: { xs: '12px', md: '28px' },
                    position: { md: 'sticky' }, top: 0, height: { md: '100vh' },
                }}
            >
                <Wordmark />
                <Box>
                    <Typography sx={{ fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase', color: MUTED }}>Workspace</Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, marginTop: '2px' }}>{org.title || org.alias}</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'column' }, gap: { xs: '16px', md: '2px' }, overflowX: { xs: 'auto', md: 'visible' } }}>
                    {SECTIONS.map(s => <NavItem key={s.id || 'home'} section={s} active={section === s.id} onClick={() => go(s.id)} />)}
                </Box>
                <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'block' } }} />
                <Box
                    role='button'
                    tabIndex={0}
                    onClick={() => navigate(`/organization/${org.alias}`)}
                    onKeyDown={e => e.key === 'Enter' && navigate(`/organization/${org.alias}`)}
                    sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: '8px', fontSize: '12px', color: MUTED, cursor: 'pointer', '&:hover': { color: INK } }}
                >
                    <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: '12px' }} />
                    Back to Monada
                </Box>
            </Box>

            <Box component='main' sx={{ flexGrow: 1, minWidth: 0, padding: { xs: '24px 16px', md: '44px 56px' }, boxSizing: 'border-box' }}>
                <Box sx={{ maxWidth: '1200px' }}>
                    {error && <Alert severity='error' sx={{ marginBottom: '16px' }}>Could not load platform data: {error}</Alert>}
                    {!data && !error && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', color: MUTED, fontSize: '13px', marginBottom: '16px' }}>
                            <CircularProgress size={14} sx={{ color: INK }} /> Loading platform data…
                        </Box>
                    )}
                    {section === '' && <Home data={data} connectors={connectors} runs={runs} go={go} />}
                    {section === 'connectors' && <ElariomConnectors connectors={connectors} org={org} accesstoken={accesstoken} onTeamsChange={onTeamsChange} onWhatsappChange={whatsapp => setData(d => ({ ...d, whatsapp }))} />}
                    {section === 'workflows' && (
                        <ElariomWorkflows
                            data={data}
                            runs={runs}
                            org={org}
                            accesstoken={accesstoken}
                            workflowId={path[1] || null}
                            openWorkflow={id => go(id ? `workflows/${id}` : 'workflows')}
                            onRoutingChange={oceanRouting => setData(d => ({ ...d, oceanRouting }))}
                            onTeamsChange={onTeamsChange}
                            isSuperAdmin={me?.email === SUPER_ADMIN}
                        />
                    )}
                    {section === 'executions' && <ElariomExecutions org={org} accesstoken={accesstoken} />}
                    {section === 'ask' && <ElariomAsk org={org} accesstoken={accesstoken} />}
                    {section === 'apps' && <Apps org={org} />}
                </Box>
            </Box>
        </Box>
    );
}

// Elariom has its own look (elariomTheme.js); the rest of Monada keeps Monada's.
function Elariom() {
    useEffect(() => { loadElariomFonts(); }, []);
    return (
        <ThemeProvider theme={elariomTheme}>
            <ElariomShell />
        </ThemeProvider>
    );
}

export default Elariom;
